import React, { FC, useEffect, useState } from "react";
import { InfoWindowF, MarkerF } from "@react-google-maps/api";
import { MarkerProps } from "./types";
import MarkerInfoWindowContent from "./MarkerInfoWindowContent";
import MarkerTooltip from "./MarkerTooltip";
import { useMapState } from "./MapStateContext";
import { useAppDispatch } from "hooks";
import { setActiveObjectId } from "app/features";
import { utils } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import {
  focusMapOnSingleVehicle,
  isValidMapCoordinate
} from "app/data/helpers";

const Marker: FC<MarkerProps> = ({
  marker,
  clusterer,
  noClustererRedraw,
  showLinkIcon
}) => {
  const { t } = useTranslation();
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [closeInfoWindowClicked, setCloseInfoWindowClicked] = useState(false);
  const [icon, setIcon] = useState<google.maps.Icon | undefined>();
  const {
    activeObjectId,
    isPrivateRide,
    isObjectDetailsPage,
    isDashboardMap,
    mapRef
  } = useMapState();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (marker) {
      const iconWithDirection = utils.getMapIcon(
        marker.iconUrl,
        marker?.direction || marker?.lastReportedDirection
      );
      setIcon(iconWithDirection);
    }
  }, [marker]);

  useEffect(() => {
    if (activeObjectId !== marker.uniqueId) {
      // Another object owns selection — never leave this popup open.
      setShowInfoWindow(false);
      setCloseInfoWindowClicked(false);
      return;
    }

    if (!closeInfoWindowClicked) {
      setShowInfoWindow(true);
    }
  }, [activeObjectId, marker.uniqueId, closeInfoWindowClicked]);

  const handleCloseInfoWindowClick = () => {
    dispatch(setActiveObjectId(null));
    window.localStorage.removeItem("selectedObjectId");
    setShowInfoWindow(false);
    setCloseInfoWindowClicked(true);
  };

  const handleIconClick = () => {
    if (
      isDashboardMap &&
      !isObjectDetailsPage &&
      isValidMapCoordinate(marker.lat, marker.lng)
    ) {
      focusMapOnSingleVehicle(mapRef, { lat: marker.lat, lng: marker.lng });
    }

    setCloseInfoWindowClicked(false);
    setShowInfoWindow(true);
    dispatch(setActiveObjectId(marker.uniqueId ?? ""));
  };

  if (isPrivateRide && isObjectDetailsPage) {
    return null;
  }

  const accessibleTitle = marker.isInactive
    ? `${marker?.name || ""} (${t("common.inactive")})`
    : marker?.name;

  return (
    <>
      <MarkerF
        position={{ lat: marker.lat || 0, lng: marker.lng || 0 }}
        onClick={() => handleIconClick()}
        icon={icon}
        title={accessibleTitle}
        onMouseOver={() => setIsTooltipVisible(true)}
        onMouseOut={() => setIsTooltipVisible(false)}
        clusterer={clusterer}
        noClustererRedraw={noClustererRedraw}
      >
        {showInfoWindow && (
          <InfoWindowF
            position={{ lat: marker.lat, lng: marker.lng }}
            onCloseClick={handleCloseInfoWindowClick}
          >
            <MarkerInfoWindowContent
              marker={{ ...marker }}
              showLinkIcon={showLinkIcon}
            />
          </InfoWindowF>
        )}
      </MarkerF>

      {isTooltipVisible && <MarkerTooltip marker={marker} />}
    </>
  );
};

export default React.memo(Marker);
